import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isToday, isPast, parseISO } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import RemindersCalendar from '../components/reminders/RemindersCalendar';
import { linkifyReminderText, extractPrimaryLink } from '../lib/reminderLinks';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import TimeInput from '../components/TimeInput';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Clock,
  Bell,
  Check,
  Trash2,
  Edit,
  Loader2,
  AlertCircle,
  Repeat,
  X
} from 'lucide-react';
import StatTile from '../components/StatTile';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to format 24hr time to 12hr time
const formatTime12hr = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};

const CATEGORY_COLORS = {
  "Personal": "bg-indigo-100 text-indigo-800 border-indigo-200",
  "Job": "bg-amber-100 text-amber-800 border-amber-200",
  "811": "bg-red-100 text-red-800 border-red-200",
  "Equipment": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Meeting": "bg-blue-100 text-blue-800 border-blue-200",
  "Other": "bg-purple-100 text-purple-800 border-purple-200"
};

const PRIORITY_COLORS = {
  "High": "border-l-red-500",
  "Medium": "border-l-amber-500",
  "Low": "border-l-green-500"
};

const PRIORITY_BADGES = {
  "High": "bg-red-100 text-red-700",
  "Medium": "bg-amber-100 text-amber-700",
  "Low": "bg-green-100 text-green-700"
};

export default function Reminders() {
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [reminders, setReminders] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [overdueReminders, setOverdueReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [time, setTime] = useState('');
  const [category, setCategory] = useState('Personal');
  const [priority, setPriority] = useState('Medium');
  const [recurringType, setRecurringType] = useState('');
  const [recurringInterval, setRecurringInterval] = useState('');
  const [recurringEndDate, setRecurringEndDate] = useState('');

  const fetchReminders = useCallback(async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const response = await axios.get(`${API}/api/reminders/calendar/${year}/${month}`);
      setReminders(response.data);
    } catch (error) {
      console.error('Failed to fetch reminders:', error);
    }
  }, [currentMonth]);

  const fetchUpcoming = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/reminders/upcoming?days=14&limit=20`);
      setUpcomingReminders(response.data);
    } catch (error) {
      console.error('Failed to fetch upcoming:', error);
    }
  }, []);

  const fetchOverdue = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/reminders/overdue`);
      setOverdueReminders(response.data);
    } catch (error) {
      console.error('Failed to fetch overdue:', error);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchReminders(), fetchUpcoming(), fetchOverdue()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchReminders, fetchUpcoming, fetchOverdue]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setDate(format(new Date(), 'yyyy-MM-dd'));
    setTime('');
    setCategory('Personal');
    setPriority('Medium');
    setRecurringType('');
    setRecurringInterval('');
    setRecurringEndDate('');
    setEditingReminder(null);
  };

  const handleOpenNew = (selectedDateObj = null) => {
    resetForm();
    if (selectedDateObj) {
      setDate(format(selectedDateObj, 'yyyy-MM-dd'));
    }
    setShowDialog(true);
  };

  const handleEdit = (reminder) => {
    setEditingReminder(reminder);
    setTitle(reminder.title);
    setDescription(reminder.description || '');
    setDate(reminder.date);
    setTime(reminder.time || '');
    setCategory(reminder.category);
    setPriority(reminder.priority);
    setRecurringType(reminder.recurring_type || '');
    setRecurringInterval(reminder.recurring_interval?.toString() || '');
    setRecurringEndDate(reminder.recurring_end_date || '');
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setSaving(true);
    try {
      const data = {
        title: title.trim(),
        description: description.trim(),
        date,
        time: time || null,
        category,
        priority,
        recurring_type: recurringType || null,
        recurring_interval: recurringInterval ? parseInt(recurringInterval) : null,
        recurring_end_date: recurringEndDate || null
      };

      if (editingReminder) {
        await axios.put(`${API}/api/reminders/${editingReminder.id}`, data);
        toast.success('Reminder updated');
      } else {
        await axios.post(`${API}/api/reminders`, data);
        toast.success('Reminder created');
      }

      setShowDialog(false);
      resetForm();
      fetchReminders();
      fetchUpcoming();
      fetchOverdue();
    } catch (error) {
      toast.error('Failed to save reminder');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (reminder) => {
    try {
      await axios.post(`${API}/api/reminders/${reminder.id}/complete`);
      toast.success('Reminder completed');
      fetchReminders();
      fetchUpcoming();
      fetchOverdue();
    } catch (error) {
      toast.error('Failed to complete reminder');
    }
  };

  const handleDelete = async (reminder) => {
    if (!window.confirm('Delete this reminder?')) return;
    
    try {
      await axios.delete(`${API}/api/reminders/${reminder.id}`);
      toast.success('Reminder deleted');
      fetchReminders();
      fetchUpcoming();
      fetchOverdue();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const handleSnooze = async (reminder, days = 1) => {
    try {
      await axios.post(`${API}/api/reminders/${reminder.id}/snooze?days=${days}`);      toast.success(`Snoozed for ${days} day${days > 1 ? 's' : ''}`);
      fetchReminders();
      fetchUpcoming();
      fetchOverdue();
    } catch (error) {
      toast.error('Failed to snooze reminder');
    }
  };

  // Drag-and-drop reschedule from the calendar view. Optimistic update —
  // the reminder visually moves to the new cell immediately, then the
  // backend PUT confirms (or we toast and refetch on failure).
  const handleReschedule = async (reminder, newDate) => {
    const previous = reminders;
    setReminders(prev => prev.map(r => r.id === reminder.id ? { ...r, date: newDate } : r));
    try {
      await axios.put(`${API}/api/reminders/${reminder.id}`, { date: newDate });
      toast.success(`Moved to ${format(parseISO(newDate), 'EEE MMM d')}`);
      fetchUpcoming();
      fetchOverdue();
    } catch (err) {
      setReminders(previous);
      toast.error('Failed to move reminder');
    }
  };

  // Calendar helpers
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad calendar to start on Sunday
  const startDay = monthStart.getDay();
  const paddedDays = [...Array(startDay).fill(null), ...calendarDays];

  // Get reminders for a specific day
  const getRemindersForDay = (dayDate) => {
    if (!dayDate) return [];
    const dateStr = format(dayDate, 'yyyy-MM-dd');
    return reminders.filter(r => r.date === dateStr);
  };

  const ReminderCard = ({ reminder, compact = false }) => (
    <div 
      className={`bg-white rounded-lg border-l-4 ${PRIORITY_COLORS[reminder.priority]} border border-slate-200 p-3 shadow-sm hover:shadow-md transition-shadow`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge className={`text-xs ${CATEGORY_COLORS[reminder.category]}`}>
              {reminder.category}
            </Badge>
            {reminder.recurring_type && (
              <Badge variant="outline" className="text-xs">
                <Repeat className="w-3 h-3 mr-1" />
                {reminder.recurring_type}
              </Badge>
            )}
          </div>
          <h4 className={`font-medium ${reminder.completed ? 'line-through text-slate-400' : 'text-slate-900'}`}>
            {reminder.title}
          </h4>
          {!compact && reminder.description && (
            <p className="text-sm text-slate-500 mt-1 break-words whitespace-pre-wrap leading-relaxed">
              {linkifyReminderText(reminder.description)}
            </p>
          )}
          {!compact && (() => {
            const primary = extractPrimaryLink(reminder.description);
            if (!primary) return null;
            return (
              <a
                href={primary.url}
                target={primary.isInternal ? '_self' : '_blank'}
                rel={primary.isInternal ? undefined : 'noopener noreferrer'}
                onClick={(e) => e.stopPropagation()}
                className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-semibold transition-colors"
                data-testid={`reminder-primary-link-${reminder.id}`}
              >
                {primary.label} →
              </a>
            );
          })()}
          {compact && (() => {
            const primary = extractPrimaryLink(reminder.description);
            if (!primary) return null;
            return (
              <a
                href={primary.url}
                target={primary.isInternal ? '_self' : '_blank'}
                rel={primary.isInternal ? undefined : 'noopener noreferrer'}
                onClick={(e) => e.stopPropagation()}
                className="mt-1 inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-800 font-semibold underline underline-offset-2"
                data-testid={`reminder-primary-link-compact-${reminder.id}`}
              >
                {primary.label} →
              </a>
            );
          })()}
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <CalendarIcon className="w-3 h-3" />
              {format(parseISO(reminder.date), 'MMM d, yyyy')}
            </span>
            {reminder.time && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTime12hr(reminder.time)}
              </span>
            )}
          </div>
        </div>
        {!compact && (
          <div className="flex flex-col gap-1">
            {!reminder.completed && (
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                onClick={() => handleComplete(reminder)}
              >
                <Check className="w-4 h-4" />
              </Button>
            )}
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-7 w-7 p-0 text-slate-500 hover:text-slate-700"
              onClick={() => handleEdit(reminder)}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={() => handleDelete(reminder)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight flex items-center gap-2">
            <Bell className="w-7 h-7 text-amber-500" />
            My Reminders
          </h1>
          <p className="text-slate-500 text-sm mt-1">Personal reminders visible only to you</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex h-9 rounded-md border border-slate-200 overflow-hidden">
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 text-sm font-medium ${viewMode === 'calendar' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              <CalendarIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 text-sm font-medium ${viewMode === 'list' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              List
            </button>
          </div>
          <Button size="sm" onClick={() => handleOpenNew()} className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold" data-testid="new-reminder-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Reminder
          </Button>
        </div>
      </div>

      {/* Overdue Reminders Alert */}
      {overdueReminders.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] text-red-800 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Overdue ({overdueReminders.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {overdueReminders.slice(0, 3).map(reminder => (
              <div key={reminder.id} className="flex items-center justify-between bg-white rounded-lg p-2 border border-red-200">
                <div>
                  <p className="font-medium text-slate-900">{reminder.title}</p>
                  <p className="text-xs text-red-600">Due: {format(parseISO(reminder.date), 'MMM d, yyyy')}</p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => handleComplete(reminder)} className="text-green-600 h-8">
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleSnooze(reminder, 1)} className="text-amber-600 h-8 text-xs">
                    +1d
                  </Button>
                </div>
              </div>
            ))}
            {overdueReminders.length > 3 && (
              <p className="text-xs text-red-600 text-center">+{overdueReminders.length - 3} more overdue</p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar or List View */}
        <div className="lg:col-span-2">
          {viewMode === 'calendar' ? (
            <RemindersCalendar
              reminders={reminders}
              anchorDate={currentMonth}
              onAnchorChange={setCurrentMonth}
              onDayClick={(d) => handleOpenNew(d)}
              onReminderClick={(r) => handleEdit(r)}
              onReminderMove={(r, newDate) => handleReschedule(r, newDate)}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-['Barlow_Condensed']">All Reminders</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {reminders.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Bell className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p>No reminders for this month</p>
                  </div>
                ) : (
                  reminders.map(reminder => (
                    <ReminderCard key={reminder.id} reminder={reminder} />
                  ))
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Upcoming Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" />
                Upcoming (14 days)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {upcomingReminders.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No upcoming reminders</p>
              ) : (
                upcomingReminders.map(reminder => (
                  <ReminderCard key={reminder.id} reminder={reminder} compact />
                ))
              )}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-['Barlow_Condensed']">Categories</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.keys(CATEGORY_COLORS).map(cat => {
                  const count = reminders.filter(r => r.category === cat).length;
                  return (
                    <Badge key={cat} className={`${CATEGORY_COLORS[cat]}`}>
                      {cat}: {count}
                    </Badge>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingReminder ? 'Edit Reminder' : 'New Reminder'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter reminder title"
                data-testid="reminder-title-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Description</Label>
                {(() => {
                  const primary = extractPrimaryLink(description);
                  if (!primary) return null;
                  return (
                    <a
                      href={primary.url}
                      target={primary.isInternal ? '_self' : '_blank'}
                      rel={primary.isInternal ? undefined : 'noopener noreferrer'}
                      className="text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 hover:bg-amber-200 text-amber-800 font-semibold transition-colors"
                      data-testid="reminder-dialog-primary-link"
                    >
                      {primary.label} →
                    </a>
                  );
                })()}
              </div>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional details..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <TimeInput
                  value={time}
                  onChange={(val) => setTime(val)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(CATEGORY_COLORS).map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Repeat className="w-4 h-4" />
                Recurring
              </Label>
              <Select value={recurringType || 'none'} onValueChange={(v) => setRecurringType(v === 'none' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Not recurring" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not recurring</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="custom">Custom interval</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {recurringType === 'custom' && (
              <div className="space-y-2">
                <Label>Repeat every X days</Label>
                <Input
                  type="number"
                  value={recurringInterval}
                  onChange={(e) => setRecurringInterval(e.target.value)}
                  placeholder="e.g., 14 for bi-weekly"
                  min="1"
                />
              </div>
            )}

            {recurringType && (
              <div className="space-y-2">
                <Label>End Date (optional)</Label>
                <Input
                  type="date"
                  value={recurringEndDate}
                  onChange={(e) => setRecurringEndDate(e.target.value)}
                />
              </div>
            )}

            <div className="flex justify-between items-center gap-3 pt-4">
              {/* Delete is only available when editing an existing reminder.
                  Hidden on the Create flow because there's nothing to delete yet. */}
              {editingReminder ? (
                <Button
                  variant="ghost"
                  onClick={async () => {
                    await handleDelete(editingReminder);
                    // handleDelete shows window.confirm and toasts on success;
                    // close the dialog if the reminder was actually removed.
                    setShowDialog(false);
                  }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="reminder-dialog-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              ) : <span />}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingReminder ? 'Save Changes' : 'Create Reminder'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
